import { ChangeEvent, ReactNode, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { format } from 'libs/dates'
import { FilterField, FiltersPanel, FiltersToggle, SearchInput, Select } from 'shared/components'
import { DatePickerRange } from 'shared/components/DatePickerRange/DatePickerRange'
import { parseDate } from 'shared/components/DatePickerRange/utils/utils'
import { useStatusOptions } from 'features/payouts/hooks/useStatusOptions'

const FILTER_PARAMS = ['status', 'created_after', 'created_before']

type FiltersSectionProps = {
	actions?: ReactNode
	handleSearch: (data: ChangeEvent<HTMLInputElement>) => void
	handleFilter: ({ id, value }: { id: string; value: string | null }) => void
}

export const FiltersSection = ({ actions, handleSearch, handleFilter }: FiltersSectionProps) => {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const statusOptions = useStatusOptions()
	const [isOpen, setIsOpen] = useState(FILTER_PARAMS.some((param) => searchParams.has(param)))
	const [resetKey, setResetKey] = useState(0)
	const [dateRange, setDateRange] = useState<[Date | undefined, Date | undefined]>([
		parseDate(searchParams.get('created_after')),
		parseDate(searchParams.get('created_before'))
	])
	const [start, end] = dateRange

	const handleDateFilter = ([from, to]: [Date | null, Date | null]) => {
		setDateRange([from ?? undefined, to ?? undefined])
		handleFilter({ id: 'created_after', value: from ? format(from, 'yyyy-MM-dd') : null })
		handleFilter({ id: 'created_before', value: to ? format(to, 'yyyy-MM-dd') : null })
	}

	const clearFilters = () => {
		const params = new URLSearchParams(searchParams)
		FILTER_PARAMS.forEach((param) => params.delete(param))
		params.delete('cursor')
		setSearchParams(params)
		setDateRange([undefined, undefined])
		setResetKey((key) => key + 1)
	}

	return (
		<div className="border-b border-border p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				{actions}
				<SearchInput
					variant="filled"
					className="min-w-0 flex-1"
					label={t('Search payouts')}
					placeholder={t('Search payouts...')}
					onChange={handleSearch}
				/>
				<FiltersToggle isOpen={isOpen} onClick={() => setIsOpen((open) => !open)} />
			</div>
			{isOpen && (
				<FiltersPanel key={resetKey} title={t('Filter payouts')} onClear={clearFilters}>
					<FilterField label={t('Status')}>
						<Select
							id="status"
							variant="outline"
							placeholder={t('All')}
							options={statusOptions}
							defaultValue={statusOptions.find(
								(option) => option.value === searchParams.get('status')
							)}
							onChange={(data) => handleFilter({ id: 'status', value: data?.value || null })}
						/>
					</FilterField>
					<FilterField label={t('Date')}>
						<DatePickerRange
							label=""
							variant="outline"
							start={start}
							end={end}
							onChange={handleDateFilter}
						/>
					</FilterField>
				</FiltersPanel>
			)}
		</div>
	)
}
